import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Youtube,
  Instagram,
  Facebook,
  Twitter,
  Podcast,
  Music2,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Plus,
  Trophy,
  Star,
  Filter,
  ChevronDown,
  Dog,
  Cat,
  Bird,
  Sparkles,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Platform icons mapping
const PLATFORM_ICONS = {
  youtube: Youtube,
  tiktok: Music2,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  podcasts: Podcast
};

const PLATFORM_COLORS = {
  youtube: 'bg-red-500 hover:bg-red-600',
  tiktok: 'bg-black hover:bg-gray-800',
  instagram: 'bg-pink-500 hover:bg-pink-600',
  facebook: 'bg-blue-600 hover:bg-blue-700',
  twitter: 'bg-sky-500 hover:bg-sky-600',
  podcasts: 'bg-purple-500 hover:bg-purple-600'
};

const PET_TYPE_ICONS = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  other: Sparkles
};

export default function SocialChannels() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [channelOfMonth, setChannelOfMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votes, setVotes] = useState({});
  const [mySubmissions, setMySubmissions] = useState([]);

  // Filters
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [selectedPetType, setSelectedPetType] = useState('all');
  const [sortBy, setSortBy] = useState('vote_score');

  // Submit dialog state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newChannel, setNewChannel] = useState({
    name: '',
    url: '',
    category: '',
    content_type: '',
    description: '',
    pet_types: ['dog']
  });

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API}/api/social-channels/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
          setContentTypes(data.content_types || []);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch channels
  useEffect(() => {
    const fetchChannels = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ sort_by: sortBy });
        if (activeTab !== 'all') params.append('category', activeTab);
        if (selectedContentType && selectedContentType !== 'all') params.append('content_type', selectedContentType);
        if (selectedPetType && selectedPetType !== 'all') params.append('pet_type', selectedPetType);

        const res = await fetch(`${API}/api/social-channels?${params}`);
        if (res.ok) {
          const data = await res.json();
          setChannels(data.channels || []);
          setChannelOfMonth(data.channel_of_month);
        }
      } catch (err) {
        console.error('Failed to fetch channels:', err);
        toast.error('Failed to load channels');
      } finally {
        setLoading(false);
      }
    };
    fetchChannels();
  }, [activeTab, selectedContentType, selectedPetType, sortBy]);

  // Fetch user's votes on channels
  useEffect(() => {
    const fetchVotes = async () => {
      if (!token || channels.length === 0) return;
      
      const voteMap = {};
      for (const channel of channels) {
        try {
          const res = await fetch(`${API}/api/social-channels/${channel.id}/my-vote`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            voteMap[channel.id] = data.vote;
          }
        } catch (err) {
          // Ignore errors
        }
      }
      setVotes(voteMap);
    };
    fetchVotes();
  }, [token, channels]);

  // Fetch user's submissions
  useEffect(() => {
    const fetchMySubmissions = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/social-channels/my/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMySubmissions(data.channels || []);
        }
      } catch (err) {
        console.error('Failed to fetch submissions:', err);
      }
    };
    fetchMySubmissions();
  }, [token]);

  // Handle vote
  const handleVote = async (channelId, vote) => {
    if (!token) {
      toast.error('Please log in to vote');
      return;
    }

    const currentVote = votes[channelId] || 0;
    const newVote = currentVote === vote ? 0 : vote;

    try {
      const res = await fetch(`${API}/api/social-channels/${channelId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ vote: newVote })
      });

      if (res.ok) {
        const data = await res.json();
        setVotes(prev => ({ ...prev, [channelId]: newVote }));
        setChannels(prev => prev.map(ch => 
          ch.id === channelId 
            ? { ...ch, upvotes: data.upvotes, downvotes: data.downvotes, vote_score: data.vote_score }
            : ch
        ));
      }
    } catch (err) {
      toast.error('Failed to vote');
    }
  };

  // Handle submit new channel
  const handleSubmit = async () => {
    if (!token) {
      toast.error('Please log in to submit channels');
      return;
    }

    if (!newChannel.name || !newChannel.url || !newChannel.category || !newChannel.content_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/social-channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newChannel)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSubmitOpen(false);
        setNewChannel({
          name: '',
          url: '',
          category: '',
          content_type: '',
          description: '',
          pet_types: ['dog']
        });
        // Refresh channels
        setChannels(prev => data.auto_approved ? [data.channel, ...prev] : prev);
        setMySubmissions(prev => [data.channel, ...prev]);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit channel');
      }
    } catch (err) {
      toast.error('Failed to submit channel');
    } finally {
      setSubmitting(false);
    }
  };

  // Channel card component
  const ChannelCard = ({ channel, showVotes = true }) => {
    const PlatformIcon = PLATFORM_ICONS[channel.category] || Youtube;
    const userVote = votes[channel.id] || 0;

    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`channel-${channel.id}`}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            {/* Platform Icon */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${PLATFORM_COLORS[channel.category] || 'bg-gray-500'}`}>
              <PlatformIcon className="w-6 h-6 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <a 
                    href={channel.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium hover:text-primary flex items-center gap-1"
                    data-testid={`channel-link-${channel.id}`}
                  >
                    {channel.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      {channel.content_type}
                    </Badge>
                    {channel.pet_types?.map(pt => {
                      const Icon = PET_TYPE_ICONS[pt] || Dog;
                      return <Icon key={pt} className="w-4 h-4 text-muted-foreground" />;
                    })}
                  </div>
                </div>

                {/* Vote buttons */}
                {showVotes && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant={userVote === 1 ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleVote(channel.id, 1)}
                      data-testid={`upvote-${channel.id}`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </Button>
                    <span className={`text-sm font-medium min-w-[2rem] text-center ${
                      channel.vote_score > 0 ? 'text-green-500' : 
                      channel.vote_score < 0 ? 'text-red-500' : 'text-muted-foreground'
                    }`}>
                      {channel.vote_score}
                    </span>
                    <Button
                      variant={userVote === -1 ? 'destructive' : 'ghost'}
                      size="sm"
                      onClick={() => handleVote(channel.id, -1)}
                      data-testid={`downvote-${channel.id}`}
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {channel.description && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {channel.description}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Social Media Channels</h1>
          <p className="text-muted-foreground mt-1">
            Discover and share the best pet content creators
          </p>
        </div>
        
        {user && (
          <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
            <DialogTrigger asChild>
              <Button data-testid="submit-channel-btn">
                <Plus className="w-4 h-4 mr-2" />
                Submit Channel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Submit a Channel</DialogTitle>
                <DialogDescription>
                  Share your favorite pet content creator with the community
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Channel Name *</Label>
                  <Input
                    id="name"
                    value={newChannel.name}
                    onChange={(e) => setNewChannel(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Dog TV"
                    data-testid="input-channel-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="url">Channel URL *</Label>
                  <Input
                    id="url"
                    value={newChannel.url}
                    onChange={(e) => setNewChannel(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://youtube.com/c/example"
                    data-testid="input-channel-url"
                  />
                </div>
                
                <div>
                  <Label>Platform *</Label>
                  <Select
                    value={newChannel.category}
                    onValueChange={(v) => setNewChannel(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Content Type *</Label>
                  <Select
                    value={newChannel.content_type}
                    onValueChange={(v) => setNewChannel(prev => ({ ...prev, content_type: v }))}
                  >
                    <SelectTrigger data-testid="select-content-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {contentTypes.map(type => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newChannel.description}
                    onChange={(e) => setNewChannel(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What makes this channel great?"
                    rows={2}
                    data-testid="input-description"
                  />
                </div>
                
                <div>
                  <Label>Pet Types</Label>
                  <div className="flex gap-4 mt-2">
                    {['dog', 'cat', 'bird', 'other'].map(pet => (
                      <label key={pet} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={newChannel.pet_types.includes(pet)}
                          onCheckedChange={(checked) => {
                            setNewChannel(prev => ({
                              ...prev,
                              pet_types: checked
                                ? [...prev.pet_types, pet]
                                : prev.pet_types.filter(p => p !== pet)
                            }));
                          }}
                        />
                        <span className="capitalize">{pet}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSubmitOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-channel-confirm">
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Channel of the Month */}
      {channelOfMonth && (
        <Card className="mb-8 border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              <CardTitle>Channel of the Month</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ChannelCard channel={channelOfMonth} showVotes={false} />
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={selectedContentType} onValueChange={setSelectedContentType}>
          <SelectTrigger className="w-40" data-testid="filter-content-type">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Content Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {contentTypes.map(type => (
              <SelectItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedPetType} onValueChange={setSelectedPetType}>
          <SelectTrigger className="w-40" data-testid="filter-pet-type">
            <Dog className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Pet Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pets</SelectItem>
            <SelectItem value="dog">Dogs</SelectItem>
            <SelectItem value="cat">Cats</SelectItem>
            <SelectItem value="bird">Birds</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40" data-testid="sort-by">
            <TrendingUp className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort By" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="vote_score">Top Voted</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="name">Alphabetical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="all" data-testid="tab-all">
            <Star className="w-4 h-4 mr-2" />
            All
          </TabsTrigger>
          {categories.map(cat => {
            const Icon = PLATFORM_ICONS[cat.id] || Youtube;
            return (
              <TabsTrigger key={cat.id} value={cat.id} data-testid={`tab-${cat.id}`}>
                <Icon className="w-4 h-4 mr-2" />
                {cat.name}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Channels List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : channels.length > 0 ? (
        <div className="space-y-4">
          {channels.map(channel => (
            <ChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Youtube className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No channels yet</h3>
            <p className="text-muted-foreground mb-4">
              Be the first to submit a channel in this category!
            </p>
            {user && (
              <Button onClick={() => setSubmitOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Submit Channel
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* My Submissions Section */}
      {user && mySubmissions.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold mb-4">My Submissions</h2>
          <div className="space-y-4">
            {mySubmissions.map(channel => (
              <Card key={channel.id} className="border-dashed">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const Icon = PLATFORM_ICONS[channel.category] || Youtube;
                        return <Icon className="w-5 h-5 text-muted-foreground" />;
                      })()}
                      <span>{channel.name}</span>
                    </div>
                    <Badge variant={channel.status === 'approved' ? 'default' : channel.status === 'pending' ? 'secondary' : 'destructive'}>
                      {channel.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
